import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import ChirpPropShapes from 'javascripts/shared/chirp_prop_shapes';
import { Waypoint } from 'react-waypoint';

import { bubEvents } from 'shared/events/bub_events';

import { CoverImageUtils } from 'javascripts/shared/utils/cover_image_utils';
import { useHover } from 'javascripts/hooks/use-hover';
import classNames from 'classnames';

import { AssetImg } from 'javascripts/components/asset_img';
import { CoverShoppingButtons } from 'javascripts/components/cover_shopping_buttons';

import hotDealBanner from 'images/HotDeal.svg';
import featuredNewReleaseBanner from 'images/FeaturedNewRelease.svg';

import styles from 'stylesheets/components/cover_image.module';

export function CoverImage(props) {
  const [showVertical, setShowVertical] = useState(!props.defer);
  const [showHorizontal, setShowHorizontal] = useState(!props.defer);

  const [hoverRef, isHovered] = useHover();
  const imageRef = useRef();

  const hasClickHandler = typeof props.onClick === 'function';

  function altText() {
    let text = 'Book cover';
    if (props.bookTitle) {
      text += ` for ${_([props.bookTitle, props.bookAuthor]).compact().join(' by ')}`;
    }
    if (props.bannerType) {
      text += ` with ${props.bannerType} banner`;
    }
    return text;
  }

  function renderPlayArrow() {
    return (
      <div className={styles.playButton} data-testid='cover-image-play-arrow'>
        <div className={styles.playButtonArrow}>
          <svg version='1.1' x='0px' y='0px' viewBox='0 0 60 60'>
            <path
              className={styles.playButtonArrowSvg}
              d='M30,0C13.458,0,0,13.458,0,30s13.458,30,30,30s30-13.458,30-30S46.542,0,30,0z M45.563,30.826l-22,15
              C23.394,45.941,23.197,46,23,46c-0.16,0-0.321-0.038-0.467-0.116C22.205,45.711,22,45.371,22,45V15c0-0.371,0.205-0.711,0.533-0.884
              c0.328-0.174,0.724-0.15,1.031,0.058l22,15C45.836,29.36,46,29.669,46,30S45.836,30.64,45.563,30.826z'
            />
          </svg>
        </div>
      </div>
    );
  }

  function renderHotDealBanner() {
    return (
      <div className={styles.coverBanner} data-testid='cover-image-hot-deal-banner' id='cover-image-hot-deal-banner'>
        <AssetImg src={hotDealBanner} attrs={{ className: styles.coverBannerImage, alt: '' }} />
      </div>
    );
  }

  function renderFeaturedNewReleaseBanner() {
    return (
      <div className={styles.coverBanner} data-testid='cover-image-featured-new-release-banner'>
        <AssetImg src={featuredNewReleaseBanner} attrs={{ className: styles.coverBannerImage, alt: '' }} />
      </div>
    );
  }

  function renderBanner() {
    switch (props.bannerType) {
      case 'featured new release':
        return renderFeaturedNewReleaseBanner();
      case 'hot deal':
        return renderHotDealBanner();
      default:
        return null;
    };
  }

  function renderImage() {
    if (!(showVertical && showHorizontal)) {
      return null;
    }

    const imgClassName = props.disabled ? styles.disabledCoverImage : styles.coverImage;
    const imageUrl = CoverImageUtils.fetchScaledImage(props.coverUrl, props.size);

    const finalImgClass = props.rounded ? `${imgClassName} ${styles.rounded}` : imgClassName;

    return <img data-testid='cover-image-image' src={imageUrl} className={finalImgClass} alt={altText()} />;
  }

  function renderShoppingButtons() {
    return (
      <div className={styles.shoppingButtons}>
        <CoverShoppingButtons
          active={isHovered}
          book={{
            id: props.bookId,
            coverUrl: props.coverUrl,
            currentProduct: props.currentProduct,
            displayAuthors: props.bookAuthor,
            displayTitle: props.bookTitle,
          }}
          returnFocusRef={imageRef}
          trackingCategory={props.trackingCategory}
          inCart={props.inCart}
          inWishlist={props.inWishlist}
        />
      </div>
    );
  }

  const trackCoverClicks = (e) => {
    bubEvents.track('Banners', 'Clicked cover', {
      label: 'Banner type',
      value: props.bookId,
      banner: props.bannerType,
    });

    if (hasClickHandler) {
      props.onClick(e);
    }
  };

  function renderCover() {
    const contentProps = {
      className: styles.coverContent,
    };

    if (props.openNewTab) {
      contentProps.target = '_blank';
      contentProps.rel = 'noopener';
    }

    if (props.linkUrl) {
      contentProps.href = props.linkUrl;

      const ariaLabel = showVertical && showHorizontal ? null : `Loading ${altText()}`;

      return (
        <a data-testid='cover-image-cover' {...contentProps} aria-label={ariaLabel} ref={imageRef} tabIndex='-1'>
          {renderImage()}
        </a>
      );
    }

    if (hasClickHandler && props.buttonLabel) {
      contentProps.role = 'button';
      contentProps['aria-label'] = props.buttonLabel;
      contentProps.style = { cursor: 'pointer' };
    }

    return (
      <div {...contentProps} ref={imageRef}>
        {renderImage()}
      </div>
    );
  }

  function renderCoverImage() {
    return (
      <div
        className={classNames(styles.coverContainer, { [styles.roundedContainer]: props.rounded })}
        onClick={trackCoverClicks}
        ref={hoverRef}
        tabIndex='0'
        data-testid='cover-image-container'
      >
        {renderCover()}
        {props.playable ? renderPlayArrow() : null}
        {props.showShoppingButtons ? renderShoppingButtons() : null}
        {renderBanner()}
      </div>
    );
  }

  if (props.defer) {
    return (
      <Waypoint onEnter={() => setShowVertical(true)}>
        <span>
          <Waypoint onEnter={() => setShowHorizontal(true)} horizontal>
            {renderCoverImage()}
          </Waypoint>
        </span>
      </Waypoint>
    );
  }

  return <>{renderCoverImage()}</>;
}

CoverImage.propTypes = {
  bookAuthor: PropTypes.string,
  bookId: PropTypes.string,
  bookTitle: PropTypes.string,
  buttonLabel: PropTypes.string,
  coverUrl: PropTypes.string.isRequired,
  currentProduct: ChirpPropShapes.product,
  defer: PropTypes.bool,
  bannerType: PropTypes.oneOf(['hot deal', 'featured new release', null]),
  linkUrl: PropTypes.string,
  onClick: PropTypes.func,
  openNewTab: PropTypes.bool,
  disabled: PropTypes.bool,
  playable: PropTypes.bool,
  inCart: PropTypes.bool,
  inWishlist: PropTypes.bool,
  showShoppingButtons: PropTypes.bool,
  size: PropTypes.oneOf(['medium', 'large']),
  trackingCategory: PropTypes.string,
  rounded: PropTypes.bool,
};

CoverImage.defaultProps = {
  onClick: null,
  defer: true,
  disabled: false,
  bannerType: null,
  playable: false,
  openNewTab: false,
  inCart: false,
  inWishlist: false,
  showShoppingButtons: false,
  size: 'large',
  trackingCategory: 'Audiobook cover',
  rounded: true,
};
